import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    MenuItemProps,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    styled,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';
import { FieldClearIconButton } from './FieldClearIconButton';

const ITEM_HEIGHT = 34;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 8,
            borderRadius: 12,
        },
    },
};

const StyledFormControl = styled(FormControl)<{ disabled?: boolean; error?: boolean }>(({ disabled, error }) => ({
    width: '100%',
    backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.pureWhite),
    borderRadius: '4px',
    pointerEvents: disabled ? 'none' : 'auto',

    '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
        borderColor: error ? getColor(EColors.red) + ' !important' : getColor(EColors.v3Dark) + ' !important',
    },
    '& .MuiFormLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '0.875rem',
        color: getColor(EColors.pureBlack, 0.5),
    },

    '& .MuiFormLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.pureBlack, 0.6),
        fontSize: '0.875rem',
        transform: 'translate(9px, -11px) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1em',
    },
}));

const StyledSelect = styled(Select<string>, {
    shouldForwardProp: prop => prop !== 'disabled' && prop !== 'error' && prop !== 'hasClearButton',
})<{ disabled?: boolean; error?: boolean; hasClearButton?: boolean }>(({ disabled, error, hasClearButton }) => ({
    pointerEvents: disabled ? 'none' : 'auto',
    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
        borderRadius: '12px',
    },

    '& .MuiSelect-select': {
        paddingRight: hasClearButton ? '4.75rem !important' : undefined,
    },

    '& .MuiOutlinedInput-input': {
        backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.v3Background),
        borderRadius: '12px',
        padding: '12.5px',
        paddingLeft: '16px',
        fontSize: '0.875rem',
    },

    '& .MuiSelect-icon': {
        right: 10,
        top: '50%',
        height: 'auto',
        maxHeight: 'none',
        marginTop: 0,
        display: 'inline-flex',
        alignItems: 'center',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
    },

    '& .MuiSelect-icon.MuiSelect-iconOpen > .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
    },

    '&.Mui-disabled': {
        color: getColor(EColors.pureBlack, 0.5),
        opacity: 0.5,

        '& .MuiSelect-select': {
            color: getColor(EColors.pureBlack),
            WebkitTextFillColor: getColor(EColors.pureBlack),
        },

        '& .MuiMenuItem-root.Mui-disabled': {
            color: getColor(EColors.pureBlack),
        },
    },
}));

const StyledInputLabel = styled(InputLabel)({
    fontSize: '0.875rem',
    lineHeight: '0.875rem',

    '&.MuiFormLabel-root': {
        top: '1.5px',
        color: getColor(EColors.pureBlack, 0.5),
    },

    '&.MuiInputLabel-shrink': {
        lineHeight: '0.75rem',
        top: 4,
    },
});

const StyledMenuItem = styled(MenuItem)<MenuItemProps>(({ theme: { spacing } }) => ({
    margin: spacing(0, 1.5),
    padding: spacing(1),
    display: 'flex',
    justifyContent: 'center',
    fontSize: '0.75rem',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: 'inherit',
    '&:hover': {
        backgroundColor: getColor(EColors.pureBlack, 0.1),
        color: getColor(EColors.pureBlack),
    },
    '&.Mui-selected': {
        backgroundColor: getColor(EColors.pureBlack),
        color: getColor(EColors.pureWhite),
    },
    '&.Mui-selected:hover': {
        backgroundColor: getColor(EColors.pureBlack, 0.9),
        color: getColor(EColors.pureWhite),
    },
}));

// Generate time options programmatically with digital format value, including :30 marks
const generateTimeOptions = () => {
    const timeOptions = [];

    // Add 12:00 AM (00:00) and 12:30 AM (00:30)
    timeOptions.push({ value: '00:00', label: '12:00 AM' });
    timeOptions.push({ value: '00:30', label: '12:30 AM' });

    // Add 1:00 AM to 11:30 AM
    for (let hour = 1; hour <= 11; hour++) {
        const formattedHour = hour.toString().padStart(2, '0');
        timeOptions.push({
            value: `${formattedHour}:00`,
            label: `${formattedHour}:00 AM`,
        });
        timeOptions.push({
            value: `${formattedHour}:30`,
            label: `${formattedHour}:30 AM`,
        });
    }

    // Add 12:00 PM (12:00) and 12:30 PM (12:30)
    timeOptions.push({ value: '12:00', label: '12:00 PM' });
    timeOptions.push({ value: '12:30', label: '12:30 PM' });

    // Add 1:00 PM to 11:30 PM
    for (let hour = 1; hour <= 11; hour++) {
        const formattedHour = hour.toString().padStart(2, '0');
        const militaryHour = (hour + 12).toString();
        timeOptions.push({
            value: `${militaryHour}:00`,
            label: `${formattedHour}:00 PM`,
        });
        timeOptions.push({
            value: `${militaryHour}:30`,
            label: `${formattedHour}:30 PM`,
        });
    }

    return timeOptions;
};

// Generate all time options
const timeOptions = generateTimeOptions();

type TimeSelectIconState = {
    showClearButton: boolean;
    onClear: () => void;
    disabled?: boolean;
};

function TimeSelectIcon({
    className,
    iconStateRef,
    ...iconProps
}: React.ComponentProps<typeof KeyboardArrowDownIcon> & {
    iconStateRef: React.MutableRefObject<TimeSelectIconState>;
}) {
    const { showClearButton, onClear, disabled } = iconStateRef.current;

    return (
        <Box
            component="span"
            className={className}
            sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '2px',
                height: '100%',
                pointerEvents: 'none',
                '& .MuiSvgIcon-root': {
                    position: 'static',
                },
                '& .MuiIconButton-root': {
                    pointerEvents: 'auto',
                },
            }}
            onMouseDown={event => event.stopPropagation()}
        >
            <KeyboardArrowDownIcon {...iconProps} />
            {showClearButton ? (
                <FieldClearIconButton onClear={onClear} disabled={disabled} ariaLabel="Clear time" />
            ) : null}
        </Box>
    );
}

function createTimeSelectIconComponent(iconStateRef: React.MutableRefObject<TimeSelectIconState>) {
    return function SelectIcon(props: React.ComponentProps<typeof KeyboardArrowDownIcon>) {
        return <TimeSelectIcon {...props} iconStateRef={iconStateRef} />;
    };
}

type TimeSelectProps = {
    label?: string;
    error?: FieldError;
    value?: string;
    disabled?: boolean;
    required?: boolean;
    sx?: SxProps<Theme>;
    clearable?: boolean;
    onChange?: (time: string) => void;
    disablePoint?: string;
    /**
     * When the field is empty, scroll the dropdown so this time value is the first visible option
     * on open (e.g. '09:00'). All times remain selectable; this only sets the initial scroll
     * position. Ignored when a value is already selected.
     */
    openToValue?: string;
};

export const TimeSelect = React.forwardRef<HTMLDivElement, TimeSelectProps>(
    ({ value, label, disabled, error, required, sx, clearable = false, disablePoint, openToValue, onChange }, ref) => {
        const handleChange = (event: SelectChangeEvent<string>) => {
            onChange?.(event.target.value);
        };

        // When the field is empty, scroll the open menu (the paper `node`) so `openToValue` sits at
        // the top. Accumulate offsetTop up the offsetParent chain to `node` so it's correct
        // regardless of which intermediate element is positioned. offsetTop ignores the Grow
        // transform, so running this on enter avoids a visible jump.
        const scrollToOpenValue = useCallback(
            (node: HTMLElement) => {
                if (value || !openToValue) return;
                const target = node.querySelector<HTMLElement>(`[data-time-value="${openToValue}"]`);
                if (!target) return;
                let top = 0;
                let el: HTMLElement | null = target;
                while (el && el !== node) {
                    top += el.offsetTop;
                    el = el.offsetParent as HTMLElement | null;
                }
                node.scrollTop = top;
            },
            [value, openToValue]
        );

        const menuProps = useMemo(
            () => ({ ...MenuProps, TransitionProps: { onEnter: scrollToOpenValue, onEntered: scrollToOpenValue } }),
            [scrollToOpenValue]
        );

        const handleClear = useCallback(() => {
            onChange?.('');
        }, [onChange]);

        const showClearButton = clearable && !!value;

        const iconStateRef = useRef<TimeSelectIconState>({
            showClearButton: false,
            onClear: handleClear,
            disabled,
        });
        iconStateRef.current = { showClearButton, onClear: handleClear, disabled };

        const SelectIcon = useMemo(() => createTimeSelectIconComponent(iconStateRef), []);

        const disablePointIndex = useMemo(() => {
            if (!disablePoint) return -1;

            return timeOptions.findIndex(option => option.value === disablePoint);
        }, [disablePoint]);

        return (
            <Box flex={1} ref={ref}>
                <StyledFormControl required={required} disabled={disabled} error={!!error} sx={sx}>
                    <StyledInputLabel>{label}</StyledInputLabel>
                    <StyledSelect
                        IconComponent={SelectIcon}
                        value={value ?? ''}
                        onChange={handleChange}
                        input={<OutlinedInput label={label} />}
                        MenuProps={menuProps}
                        disabled={disabled}
                        error={!!error}
                        hasClearButton={showClearButton}
                        data-testid="time-picker"
                    >
                        {timeOptions.map((timeOption, index) => (
                            <StyledMenuItem
                                key={timeOption.value}
                                value={timeOption.value}
                                data-time-value={timeOption.value}
                                disabled={disablePointIndex !== -1 && index <= disablePointIndex}
                            >
                                {timeOption.label}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </StyledFormControl>
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error.message}
                    </Typography>
                )}
            </Box>
        );
    }
);
